import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cn } from "../../lib/utils";

type ToastTone = "success" | "danger" | "warning" | "info";

interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastApi {
  success: (title: string, description?: string) => void;
  danger: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

const META: Record<ToastTone, { icon: typeof Info; bar: string; text: string }> = {
  success: { icon: CheckCircle2, bar: "border-l-success", text: "text-success" },
  danger:  { icon: XCircle,      bar: "border-l-danger",  text: "text-danger" },
  warning: { icon: AlertTriangle,bar: "border-l-warning", text: "text-warning" },
  info:    { icon: Info,         bar: "border-l-brand",   text: "text-brand" },
};

const uid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback(
    (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );

  const push = useCallback(
    (tone: ToastTone, title: string, description?: string) => {
      const id = uid();
      setToasts((prev) => [...prev.slice(-4), { id, tone, title, description }]);
      window.setTimeout(() => dismiss(id), tone === "danger" ? 6000 : 4000);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (t, d) => push("success", t, d),
      danger: (t, d) => push("danger", t, d),
      warning: (t, d) => push("warning", t, d),
      info: (t, d) => push("info", t, d),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Viewport */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-6 right-6 z-[60] flex w-80 flex-col gap-2"
      >
        {toasts.map((t) => {
          const { icon: Icon, bar, text } = META[t.tone];
          return (
            <div
              key={t.id}
              role="status"
              className={cn(
                "toast-enter pointer-events-auto flex items-start gap-3 rounded-xl border border-line border-l-4 bg-elevated p-4 shadow-lg",
                bar,
              )}
            >
              <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", text)} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink">{t.title}</p>
                {t.description && (
                  <p className="mt-0.5 text-xs text-ink-2">{t.description}</p>
                )}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="rounded p-0.5 text-ink-3 transition-colors hover:bg-soft hover:text-ink"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
